import React from 'react';
import cn from 'classnames';
import styles from './SchemaForm.module.scss';
import messages from './messages';
import { ISubmitEvent, withTheme } from '@rjsf/core';
import Button from '../Button';
import InfoIcon from 'src/common/icons/Info';
import { motion, useAnimation, useMotionValue } from 'framer-motion';
import { FormattedMessage } from 'react-intl';
import {  Plus, TrashSimple } from 'src/common/icons';
import DownArrow from 'src/common/icons/ArrowDown';
import UpArrow from 'src/common/icons/ArrowUp';


type SchemaFormProps = {
  className?: string;
  schema?: any;
  formData: any;
  onSubmit: ((e: ISubmitEvent<any>, nativeEvent: React.FormEvent<HTMLFormElement>) => any) | undefined;
  onCancel: () => void;
};

const constructUiSchema = (json: any) => {
  const uiSchema: { [key: string]: any } = {};
  // TODO: Replace en with actual language or fallback to browser or fallback to en
  Object.entries(json?.properties ?? {}).forEach(([key, value]: [string, any]) => {
    uiSchema[key] = {
      'ui:placeholder': value?.filespin_properties?.placeholder?.en,
      'ui:help': value?.filespin_properties?.hint?.en,
      'ui:disabled': value?.filespin_properties?.ui?.disabled,
      'ui:hidden': value?.filespin_properties?.ui?.hidden,
      'ui:title': value?.filespin_properties?.title?.en || value?.title,
    };
    // String arrays (tags, keywords, regions, …) use a compact chip editor
    // instead of the verbose one-input-per-item array UI.
    if (value?.type === 'array' && value?.items?.type === 'string') {
      uiSchema[key]['ui:field'] = TagsArrayField;
    }
  });
  uiSchema['ui:order'] = Object.entries(json?.properties ?? {})
    .sort(
      ([key_a, value_a]: [string, any], [key_b, value_b]: [string, any]) =>
        value_a?.filespin_properties?.ui?.order - value_b?.filespin_properties?.ui?.order,
    )
    .map(([key]) => key);

  return uiSchema;
};

// Compact chip editor for arrays of strings: chips with an inline remove
// button and a single "add" input, instead of one full-width input plus
// move/delete buttons per item.
const TagsArrayField = (props: any) => {
  const { schema, uiSchema, formData, onChange, disabled, readonly, name } = props;
  const [newTag, setNewTag] = React.useState('');

  const tags: string[] = Array.isArray(formData) ? formData : [];
  const title = uiSchema?.['ui:title'] || schema?.title || name;
  const canEdit = !disabled && !readonly;

  const addTag = () => {
    const tag = newTag.trim();
    if (!tag) return;
    setNewTag('');
    if (tags.includes(tag)) return;
    onChange([...tags, tag]);
  };

  return (
    <div>
      <h3 className={styles.schemaFormTitle}>{title}</h3>
      <div className={styles.tagsField}>
        {tags.map((tag, index) => (
          <span className={styles.tagChip} key={`${tag}-${index}`}>
            {String(tag)}
            {canEdit && (
              <button
                type="button"
                className={styles.tagChipRemove}
                onClick={() => onChange(tags.filter((_, i) => i !== index))}
                aria-label={`Remove ${tag}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {canEdit && (
          <input
            className={styles.tagInput}
            type="text"
            value={newTag}
            placeholder={uiSchema?.['ui:placeholder'] || 'Add…'}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            onBlur={addTag}
          />
        )}
      </div>
    </div>
  );
};

const ObjectFieldTemplate = ({ properties, title }: { TitleField: any; properties: any; title: string }) => {
  return (
    <React.Fragment>
      <h3 className={styles.schemaFormTitle}>{title}</h3>
      <div className={styles.formContent}>
        {properties.map((prop: any) => (
          <React.Fragment key={prop.content.key}>{prop.content}</React.Fragment>
        ))}
      </div>
    </React.Fragment>
  );
};

const hintAnimation = {
  visible: {
    height: 'auto',
    opacity: 1,
  },
  hidden: {
    height: 0,
    opacity: 0,
  },
};

function CustomFieldTemplate(props: any) {
  const { label, help, description, errors, children, displayLabel, schema } = props;

  const showHint = useMotionValue('hidden');

  const controls = useAnimation();

  const toggleHint = () => {
    controls.start(showHint.get() === 'hidden' ? 'visible' : 'hidden');
    showHint.set(showHint.get() === 'hidden' ? 'visible' : 'hidden');
  };

  if (schema.type === 'object') {
    return children;
  }

  // For labeled fields, both the hint and the description are folded behind
  // the info toggle to keep the form compact. Unlabeled fields (e.g. arrays,
  // which render their own title) keep the description visible since there is
  // no label row to host the toggle.
  const hasToggle = displayLabel && (help || description);
  const isReadonly = schema?.filespin_properties?.ui?.readonly;

  return (
    <div className={isReadonly ? styles.Filespin_internal_keys_formGroup : styles.formGroup}>
      {displayLabel && (
        <label className={styles.formLabel}>
          {label}
          {hasToggle && (
            <button type="button" onClick={toggleHint}>
              <InfoIcon />
            </button>
          )}
        </label>
      )}
      {hasToggle && (
        <motion.div initial={'hidden'} animate={controls} variants={hintAnimation} style={{ overflow: 'hidden' }}>
          {help && (
            <div className={styles.formHelp}>
              <InfoIcon fontSize={24} />
              {help}
            </div>
          )}
          {description && <div className={styles.formDescription}>{description}</div>}
        </motion.div>
      )}
      {isReadonly ? <div className={styles.fieldForm}>{children}</div> : children}
      {!displayLabel && description && <div className={styles.formDescription}>{description}</div>}
      {errors && <div className={styles.formErrors}>{errors}</div>}
    </div>
  );
}
// Fallback template for arrays of non-string items: one compact row per
// item with small ghost icon actions, instead of large pill buttons.
function ArrayFieldTemplate(props: any) {
  return (
    <React.Fragment>
      <h3 className={styles.schemaFormTitle}>{props.title}</h3>
      <div className={styles.arrayItems}>
        {props.items &&
          props.items.map((element: any) => (
            <div key={element.key} className={styles.arrayItem}>
              <div className={styles.arrayItemContent}>{element.children}</div>
              <div className={styles.arrayItemActions}>
                {element.hasMoveUp && (
                  <button
                    type="button"
                    onClick={element.onReorderClick(element.index, element.index - 1)}
                    aria-label="Move up"
                  >
                    <UpArrow />
                  </button>
                )}
                {element.hasMoveDown && (
                  <button
                    type="button"
                    onClick={element.onReorderClick(element.index, element.index + 1)}
                    aria-label="Move down"
                  >
                    <DownArrow />
                  </button>
                )}
                <button
                  type="button"
                  className={styles.arrayItemDelete}
                  onClick={element.onDropIndexClick(element.index)}
                  aria-label="Remove item"
                >
                  <TrashSimple />
                </button>
              </div>
            </div>
          ))}
        {props.canAdd && (
          <button type="button" onClick={props.onAddClick} className={styles.addFieldButton}>
            <Plus /> <span>Add Item</span>
          </button>
        )}
      </div>
    </React.Fragment>
  );
}

export const SchemaForm: React.FC<SchemaFormProps> = ({ className, schema, formData, onSubmit, onCancel, ...rest }) => {
  const ThemedForm = withTheme({
    className: 'schemaForm',
  });

  return (
    <div className={cn([styles.schemaFormContainer, className])}>
      <ThemedForm
        ArrayFieldTemplate={ArrayFieldTemplate}
        ObjectFieldTemplate={ObjectFieldTemplate}
        schema={schema}
        uiSchema={constructUiSchema(schema)}
        formData={formData}
        noValidate={false}
        onSubmit={onSubmit}
        FieldTemplate={CustomFieldTemplate}
        noHtml5Validate
        showErrorList={false}
      >
        <div className={styles.formButtonsContainer}>
          <Button size="small" mode="tertiary" type="button" onClick={onCancel}>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button type="submit" size="small" mode="primary">
            <FormattedMessage {...messages.submit} />
          </Button>
        </div>
      </ThemedForm>
    </div>
  );
};

export default SchemaForm;
